export const MEDIA_WRAPPER_SELECTOR =
  ".editor-image-wrapper, .editor-icon-wrapper";

let activeDrag = null;
let listenersBound = false;

const getDropRange = (clientX, clientY) => {
  if (document.caretRangeFromPoint) {
    return document.caretRangeFromPoint(clientX, clientY);
  }
  if (document.caretPositionFromPoint) {
    const position = document.caretPositionFromPoint(clientX, clientY);
    if (position) {
      const range = document.createRange();
      range.setStart(position.offsetNode, position.offset);
      range.collapse(true);
      return range;
    }
  }
  return null;
};

const clearDropMarker = (editorEl) => {
  editorEl?.querySelectorAll(".editor-media-drop-marker").forEach((el) => el.remove());
};

const showDropMarker = (editorEl, range) => {
  clearDropMarker(editorEl);
  if (!range) return;
  const marker = document.createElement("span");
  marker.className = "editor-media-drop-marker";
  marker.innerHTML = "&#8203;";
  try {
    range.insertNode(marker);
  } catch {
    marker.remove();
  }
};

const onDocumentMouseMove = (e) => {
  if (!activeDrag) return;
  const { wrapper, editorEl, mode, isIcon, startX, startY, startWidth } = activeDrag;
  const img = wrapper.querySelector("img");
  if (!img) return;

  if (mode === "resize") {
    const dx = e.clientX - startX;
    const dy = e.clientY - startY;
    const delta = Math.max(dx, dy);
    const editorWidth = editorEl?.clientWidth || 600;
    const maxW = isIcon ? 128 : editorWidth;
    const minW = isIcon ? 16 : 48;
    const newWidth = Math.min(maxW, Math.max(minW, startWidth + delta));
    img.style.width = `${Math.round(newWidth)}px`;
    img.style.height = "auto";
    img.style.maxWidth = "none";
    if (isIcon) {
      img.style.maxHeight = `${Math.round(newWidth)}px`;
    }
    return;
  }

  const range = getDropRange(e.clientX, e.clientY);
  if (range && editorEl?.contains(range.startContainer)) {
    showDropMarker(editorEl, range);
  } else {
    clearDropMarker(editorEl);
  }
};

const onDocumentMouseUp = (e) => {
  if (!activeDrag) return;

  const { wrapper, editorEl, onUpdate, mode } = activeDrag;
  wrapper.classList.remove("editor-media-dragging");
  clearDropMarker(editorEl);

  if (mode === "move") {
    const range = getDropRange(e.clientX, e.clientY);
    if (
      range &&
      editorEl?.contains(range.startContainer) &&
      !wrapper.contains(range.startContainer)
    ) {
      try {
        if (wrapper.parentNode) {
          wrapper.parentNode.removeChild(wrapper);
        }
        range.insertNode(wrapper);
        const spacer = document.createTextNode("\u00A0");
        range.setStartAfter(wrapper);
        range.collapse(true);
        range.insertNode(spacer);
      } catch {
        // keep wrapper in place if insert fails
      }
    }
  }

  onUpdate?.();
  activeDrag = null;
};

const bindDocumentListeners = () => {
  if (listenersBound) return;
  document.addEventListener("mousemove", onDocumentMouseMove);
  document.addEventListener("mouseup", onDocumentMouseUp);
  listenersBound = true;
};

const startInteraction = (e, wrapper, editorEl, onUpdate, mode, isIcon) => {
  e.preventDefault();
  e.stopPropagation();

  const img = wrapper.querySelector("img");
  if (!img || !editorEl) return;

  const rect = img.getBoundingClientRect();
  activeDrag = {
    wrapper,
    editorEl,
    onUpdate,
    mode,
    isIcon,
    startX: e.clientX,
    startY: e.clientY,
    startWidth: rect.width || img.offsetWidth || (isIcon ? 48 : 200),
  };

  if (mode === "move") {
    wrapper.classList.add("editor-media-dragging");
  }

  bindDocumentListeners();
};

const createHandle = (className, title) => {
  const handle = document.createElement("span");
  handle.className = className;
  handle.setAttribute("contenteditable", "false");
  handle.title = title;
  return handle;
};

export const setupMediaWrapper = (wrapper, { editorEl, onUpdate }) => {
  if (!wrapper || wrapper.dataset.mediaReady === "true") return;

  const isIcon = wrapper.classList.contains("editor-icon-wrapper");

  if (!wrapper.querySelector(".editor-media-drag-handle")) {
    const dragHandle = createHandle("editor-media-drag-handle", "Drag to reposition");
    dragHandle.textContent = "⠿";
    wrapper.insertBefore(dragHandle, wrapper.firstChild);
  }

  const dragHandle = wrapper.querySelector(".editor-media-drag-handle");
  const existingResize = wrapper.querySelector(".editor-media-resize-handle");
  if (isIcon && existingResize) {
    existingResize.remove();
  }

  let resizeHandle = existingResize;
  if (!isIcon && !resizeHandle) {
    resizeHandle = createHandle("editor-media-resize-handle", "Drag to resize");
    wrapper.appendChild(resizeHandle);
  }

  dragHandle.onmousedown = (e) =>
    startInteraction(e, wrapper, editorEl, onUpdate, "move", isIcon);

  if (!isIcon && resizeHandle) {
    resizeHandle.onmousedown = (e) =>
      startInteraction(e, wrapper, editorEl, onUpdate, "resize", isIcon);
  }

  wrapper.dataset.mediaReady = "true";
};

export const attachAllMediaWrappers = (editorEl, onUpdate) => {
  if (!editorEl) return;
  editorEl.querySelectorAll(MEDIA_WRAPPER_SELECTOR).forEach((wrapper) => {
    setupMediaWrapper(wrapper, { editorEl, onUpdate });
  });
};

export const teardownMediaInteractions = () => {
  document.removeEventListener("mousemove", onDocumentMouseMove);
  document.removeEventListener("mouseup", onDocumentMouseUp);
  listenersBound = false;
  activeDrag = null;
};
